/**
 * Experience: years on a form, months in the column.
 *
 * Recruiters and clients talk in years — "three to five" — and every
 * experience column in the schema stores months. The conversion belongs at the
 * form boundary rather than in either of them: the API, the importer and the
 * seed all speak months, and a column renamed to years would be a migration
 * that bought a label.
 *
 * Its own module so it can be tested. The requirement form is the only caller
 * today, and the rule it encodes — that typing 3 must store 36 — is exactly
 * the kind of thing that is quietly wrong for a year.
 */

/**
 * Whole months for a number of years typed into a form.
 *
 * Blank stays null rather than becoming zero: "no minimum stated" and "no
 * experience required" are different claims, and the column allows both.
 *
 * Rounded, because the column is an integer. Half-years are the only fraction
 * anybody types and 0.5 lands exactly on 6; anything stranger is rounded
 * rather than refused, since a requirement is a description and not an
 * arithmetic exercise.
 */
export function monthsFromYears(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;

  const years = Number(trimmed);
  if (!Number.isFinite(years)) return null;

  return Math.round(years * 12);
}

/**
 * The stored months back as years, for a form that has to show them again.
 *
 * One decimal, and only where it earns it: 18 months reads as 1.5 rather than
 * being floored to 1, which would silently discard six months the moment
 * somebody opened a requirement and saved it. 24 reads as 24, not 24.0.
 *
 * Nothing calls this yet — no screen edits a requirement — and it is written
 * alongside its inverse rather than after the bug, because the two have to
 * agree and the second one is always added in a hurry.
 */
export function yearsFromMonths(months: number | null): string {
  if (months === null) return '';

  const years = months / 12;
  return Number.isInteger(years) ? String(years) : years.toFixed(1);
}

/**
 * A stored month count as years and months: 101 -> `8y 5m`.
 *
 * The candidates table printed the raw total -- `72 mo`, `101 mo` -- which is
 * the column's unit and not how anybody says it. Nobody describes somebody as
 * having a hundred and one months of experience.
 *
 * Capital Y and M. Lower case reads as a unit abbreviation -- `6y` beside
 * `18,000` looks like a measurement -- and these sit in a column of proper
 * nouns and figures where the capital carries better. One decision, applied
 * wherever a month count is rendered: this helper, the candidate detail panel
 * that shares it, and the Open Jobs list that now defers to it.
 *
 * Both parts only when both are non-zero, so a round figure reads `6Y` rather
 * than `6Y 0M`, and under a year reads `5M` rather than `0Y 5M`.
 *
 * Zero is `Fresher`, which is the word this application already uses for it on
 * the Open Jobs list rather than a term invented here. It is a real and
 * different claim from "nobody asked": null returns null, and the caller keeps
 * its own blank.
 */
export function experienceYearsMonths(
  months: number | null | undefined,
): string | null {
  if (months === null || months === undefined) return null;
  if (months === 0) return 'Fresher';
  const years = Math.floor(months / 12);
  const rest = months % 12;
  if (years === 0) return `${rest}M`;
  return rest === 0 ? `${years}Y` : `${years}Y ${rest}M`;
}
